import type { RefreshClock } from "./resourceRefreshCoordinator"

export interface RealtimeConnection {
  start(): Promise<void>
  stop(): Promise<void>
  invoke(method: string, request: unknown): Promise<unknown>
  on(method: string, handler: (type: string, payload: unknown) => void): void
  onclose(handler: () => void): void
  onreconnecting(handler: () => void): void
  onreconnected(handler: () => void): void
}
/** A capability for one connected incarnation, invalidated even on same-socket reconnect. */
export interface RealtimeChannel {
  invoke(method: "SubscribeBenefit" | "UnsubscribeBenefit", request: unknown): Promise<unknown>
}
export type RealtimeStatus = "stopped" | "connecting" | "connected" | "reconnecting" | "unavailable"
const defaultClock: RefreshClock = {
  now: () => Date.now(),
  schedule: (callback, ms) => { const timer = setTimeout(callback, ms); return () => clearTimeout(timer) },
}

/** One session-owned connection. React components never own sockets or retry timers. */
export function createRealtimeClient(ports: {
  create(session: string): RealtimeConnection
  notification(type: string, payload: unknown): void
  status(status: RealtimeStatus): void
  reconnected(): void
}, options: { clock?: RefreshClock; random?: () => number; maxRetries?: number } = {}) {
  const clock = options.clock ?? defaultClock
  let session: string | null = null
  let connection: RealtimeConnection | null = null
  let startPromise: Promise<void> | null = null
  let transition: Promise<void> | null = null
  let stopping = Promise.resolve()
  let epoch = 0, retries = 0
  let status: RealtimeStatus = "stopped"
  let cancelRetry: (() => void) | undefined
  let channel: RealtimeChannel | null = null
  const channelHandlers = new Set<(channel: RealtimeChannel | null) => void>()
  const notificationHandlers = new Set<(channel: RealtimeChannel, type: string, payload: unknown) => void>()
  function closeChannel() {
    if (!channel) return
    channel = null
    for (const handler of channelHandlers) handler(null)
  }
  const report = (value: RealtimeStatus) => {
    if (value !== "connected") closeChannel()
    status = value; ports.status(value)
  }
  function connected(current: RealtimeConnection) {
    const owned: RealtimeChannel = { async invoke(method, request) {
      if (channel !== owned) throw new Error("Realtime connection changed.")
      const result = await current.invoke(method, request)
      if (channel !== owned) throw new Error("Realtime connection changed.")
      return result
    } }
    channel = owned
    report("connected")
    for (const handler of channelHandlers) handler(owned)
  }

  function retry(expected: number) {
    if (expected !== epoch || !session || cancelRetry || retries >= (options.maxRetries ?? 6)) return
    const delay = Math.min(30_000, 1000 * 2 ** retries++) + Math.floor((options.random ?? Math.random)() * 250)
    cancelRetry = clock.schedule(() => { cancelRetry = undefined; void connect() }, delay)
  }
  async function connect(): Promise<void> {
    if (startPromise) return startPromise
    if (!session || !connection) return
    const current = connection, expected = epoch
    report("connecting")
    const pending = (async () => {
      try {
        await current.start()
        if (expected !== epoch) return
        retries = 0; connected(current)
      } catch {
        if (expected !== epoch) return
        report("unavailable"); retry(expected)
      }
    })()
    startPromise = pending
    try { await pending } finally { if (startPromise === pending) startPromise = null }
  }
  function retire() {
    epoch++
    closeChannel()
    cancelRetry?.(); cancelRetry = undefined
    const old = connection
    connection = null
    startPromise = null
    transition = null
    // Serialize replacement behind actual stop, including pending negotiation.
    stopping = stopping.then(() => old?.stop())
  }
  return {
    start(key: string): Promise<void> {
      if (key === session) return transition ?? startPromise ?? Promise.resolve()
      retire()
      const expected = epoch
      session = key
      retries = 0
      const pending = (async () => {
        await stopping
        if (expected !== epoch) return
        const current = ports.create(key)
        connection = current
        current.on("ReceiveNotification", (type, payload) => {
          if (epoch !== expected) return
          if (channel) for (const handler of notificationHandlers) handler(channel, type, payload)
          ports.notification(type, payload)
        })
        current.onreconnecting(() => { if (epoch === expected) report("reconnecting") })
        current.onreconnected(() => {
          if (epoch !== expected) return
          retries = 0; connected(current); ports.reconnected()
        })
        current.onclose(() => {
          if (epoch !== expected) return
          report("unavailable"); retry(expected)
        })
        await connect()
      })()
      transition = pending
      return pending.catch(error => {
        if (expected === epoch) report("unavailable")
        throw error
      }).finally(() => { if (transition === pending) transition = null })
    },
    stop(): Promise<void> {
      session = null
      retire()
      report("stopped")
      return stopping
    },
    watchChannel(handler: (channel: RealtimeChannel | null) => void) {
      channelHandlers.add(handler); handler(channel)
      return () => { channelHandlers.delete(handler) }
    },
    watchNotification(handler: (channel: RealtimeChannel, type: string, payload: unknown) => void) {
      notificationHandlers.add(handler)
      return () => { notificationHandlers.delete(handler) }
    },
    recover() {
      if (!session || !connection || startPromise || status !== "unavailable") return
      cancelRetry?.(); cancelRetry = undefined
      retries = 0
      void connect()
    },
  }
}
